/**
 * SearchableDropdown — a small typeahead with a two-group layout.
 *
 * Purpose
 *   Used in the order forms (CreateOrderDialog, OrderDetailModal) to pick
 *   Sage codes (warehouses, etc.) by English name *or* code. The key UX
 *   requirement from the 3/10/2026 meeting: users want to search on English
 *   descriptions, not memorize codes.
 *
 * Behavior
 *   - Items are split into "highlighted" (primary/active) and "other". The
 *     highlighted group is always visible when the popover is open.
 *   - The "other" group is collapsed by default behind a **Show all (N)**
 *     toggle. When the user clicks it, the other items render in place.
 *   - The query filters *both* groups on code AND label (case-insensitive).
 *     If the query matches something in the "other" group, the toggle
 *     indicates how many matches are hidden so the user knows to expand.
 *   - Free-text fallback: if the user types a value that doesn't match any
 *     item and blurs the input, the raw value is committed via `onChange`.
 *     This keeps the form forward-compatible with brand-new Sage codes.
 *   - Click-outside closes the popover. Escape closes it without committing.
 *
 * This component deliberately has NO external styling dependency beyond the
 * inline-styled popover — callers pass `className` / `style` for the input
 * so it slots into both the CreateOrderDialog and the OrderDetailModal
 * without conflicting with their existing CSS modules.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import {
  matches,
  partitionItems,
  shouldAutoExpandOther as computeShouldAutoExpandOther,
} from './searchableDropdownUtils';

export interface DropdownItem {
  /** Underlying value (e.g. warehouse code). */
  value: string;
  /** English description shown in the list. */
  label: string;
  /** True when this item should appear in the primary/active group. */
  highlighted: boolean;
  /** Optional trailing meta text (e.g. item count from the API). */
  meta?: string;
}

export interface DropdownRecord {
  /** The data-action of every option row, e.g. order_ship_via_pick. */
  action: string;
  /** The values key the chosen code is recorded under, e.g. ship_via. */
  valueKey: string;
  /** Ids the pick acts on, by data-target suffix: { 'order-id': order.id }. */
  targets?: Record<string, string | undefined>;
}

export interface SearchableDropdownProps {
  /** Current committed value. */
  value: string;
  /** Called when the user commits a new value (selection or blur). */
  onChange: (next: string) => void;
  /** All items. The component splits them by `highlighted`. */
  items: readonly DropdownItem[];
  /** Input placeholder. */
  placeholder?: string;
  /** Max length of the input (e.g. 3 for warehouse codes). */
  maxLength?: number;
  /** Disables the input. */
  disabled?: boolean;
  /** Input CSS class — caller controls colors/spacing. */
  inputClassName?: string;
  /** Inline style override for the input. */
  inputStyle?: React.CSSProperties;
  /** Label for the primary group header. */
  highlightedLabel?: string;
  /** Label for the secondary group header. */
  otherLabel?: string;
  /** Test id for targeting in unit/E2E tests. */
  testId?: string;
  /** If true, the value is always uppercased before commit (default: true). */
  uppercase?: boolean;
  /**
   * How the shared recorder names a pick from this list. Without it the
   * library names each option by its label (a carrier, a warehouse, a
   * salesperson's name), one action per label; with it every pick is one
   * `action` row whose `values[valueKey]` is the chosen code and whose
   * targets are `data-target-<k>` for each `targets` key (kanban c2632a0b).
   */
  record?: DropdownRecord;
  /** Marks the input invalid (sets aria-invalid) — caller styles the wrapper. */
  invalid?: boolean;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  value,
  onChange,
  items,
  placeholder,
  maxLength,
  disabled,
  inputClassName,
  inputStyle,
  highlightedLabel = 'Active',
  otherLabel = 'Other',
  testId,
  uppercase = true,
  invalid,
  record,
}) => {
  // data-target-* on the popover: the library reads a control's ancestors.
  const targetAttrs: Record<string, string> = {};
  for (const [k, v] of Object.entries(record?.targets ?? {})) {
    if (v) targetAttrs[`data-target-${k}`] = v;
  }
  const [query, setQuery] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [showOther, setShowOther] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Keep input text in sync if the committed value changes externally
  // (e.g. after a form reset).
  useEffect(() => {
    setQuery(value);
  }, [value]);

  const { highlighted, other } = useMemo(() => partitionItems(items), [items]);

  const filteredHighlighted = useMemo(
    () => highlighted.filter((i) => matches(i, query)),
    [highlighted, query],
  );
  const filteredOther = useMemo(
    () => other.filter((i) => matches(i, query)),
    [other, query],
  );

  // If the query matches *only* "other" items, auto-expand so the user
  // isn't looking at an empty list wondering where their match went.
  const otherExpanded =
    showOther || computeShouldAutoExpandOther(query, filteredHighlighted, filteredOther);

  const commit = useCallback(
    (next: string) => {
      const normalized = uppercase ? next.trim().toUpperCase() : next.trim();
      if (normalized !== value) {
        onChange(normalized);
      }
      setQuery(normalized);
      setIsOpen(false);
      setShowOther(false);
    },
    [onChange, uppercase, value],
  );

  // Click outside to close without committing the raw text (so mis-clicks
  // don't nuke a committed value).
  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
    setShowOther(false);
    setQuery(value);
  }, [value]);
  useClickOutside(containerRef, handleClickOutside, isOpen);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      // An open popover consumes the key (hosts that close on Escape skip a
      // spent one); a closed popover lets it through to close the host.
      if (isOpen) e.preventDefault();
      setIsOpen(false);
      setShowOther(false);
      setQuery(value);
    } else if (e.key === 'Enter') {
      // Commit the first highlighted match if any, otherwise raw text.
      const first = filteredHighlighted[0] ?? filteredOther[0];
      commit(first ? first.value : query);
    }
  };

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative' }}
      data-testid={testId}
    >
      <input
        type="text"
        value={query}
        onChange={(e) => {
          const raw = e.target.value;
          setQuery(uppercase ? raw.toUpperCase() : raw);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => {
          // Delay so a click on a result button still registers.
          setTimeout(() => {
            if (!containerRef.current) return;
            if (!document.activeElement) return;
            if (!containerRef.current.contains(document.activeElement)) {
              commit(query);
            }
          }, 120);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        className={inputClassName}
        style={inputStyle}
        aria-invalid={invalid || undefined}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      {isOpen && !disabled && (
        <div
          role="listbox"
          data-testid={testId ? `${testId}-popover` : undefined}
          {...targetAttrs}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: 240,
            maxWidth: 360,
            maxHeight: 300,
            overflowY: 'auto',
            background: 'var(--cm-white, #ffffff)',
            border: '1px solid var(--cm-gray-200, #e5e7eb)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 1000,
          }}
        >
          <GroupHeader label={highlightedLabel} />
          {filteredHighlighted.length === 0 ? (
            <EmptyRow text="No matches in active list" />
          ) : (
            filteredHighlighted.map((item) => (
              <ResultRow
                key={`h-${item.value}`}
                item={item}
                highlight
                onSelect={commit}
                testId={testId}
                record={record}
              />
            ))
          )}

          <GroupHeader
            label={otherLabel}
            toggleText={
              otherExpanded
                ? `Hide (${filteredOther.length})`
                : `Show all (${filteredOther.length})`
            }
            onToggle={() => setShowOther((s) => !s)}
            toggleDisabled={filteredOther.length === 0}
            toggleExpanded={otherExpanded}
            toggleAction={record ? `${record.action}_other_toggle` : undefined}
          />

          {otherExpanded &&
            (filteredOther.length === 0 ? (
              <EmptyRow text="No additional matches" />
            ) : (
              filteredOther.map((item) => (
                <ResultRow
                  key={`o-${item.value}`}
                  item={item}
                  highlight={false}
                  onSelect={commit}
                  testId={testId}
                  record={record}
                />
              ))
            ))}
        </div>
      )}
    </div>
  );
};

function GroupHeader({
  label,
  toggleText,
  onToggle,
  toggleDisabled,
  toggleExpanded,
  toggleAction,
}: {
  label: string;
  toggleText?: string;
  onToggle?: () => void;
  toggleDisabled?: boolean;
  toggleExpanded?: boolean;
  /** Recorder name for the Show all / Hide toggle (its text carries a count). */
  toggleAction?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 10px',
        background: 'var(--cm-gray-50, #f9fafb)',
        borderBottom: '1px solid var(--cm-gray-200, #e5e7eb)',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'var(--cm-gray-500, #6b7280)',
      }}
    >
      <span>{label}</span>
      {toggleText && onToggle && (
        <button
          type="button"
          onMouseDown={(e) => {
            // Prevent the parent input's blur from firing before our click.
            e.preventDefault();
          }}
          onClick={onToggle}
          disabled={toggleDisabled}
          aria-expanded={toggleExpanded}
          data-action={toggleAction}
          style={{
            border: 'none',
            background: 'none',
            color: toggleDisabled
              ? 'var(--cm-gray-400, #9ca3af)'
              : 'var(--cm-blue-primary, #0046AD)',
            cursor: toggleDisabled ? 'default' : 'pointer',
            fontSize: 11,
            fontWeight: 700,
            padding: '2px 6px',
            borderRadius: 4,
          }}
        >
          {toggleText}
        </button>
      )}
    </div>
  );
}

function EmptyRow({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: '10px 12px',
        fontSize: 12,
        color: 'var(--cm-gray-500, #6b7280)',
      }}
    >
      {text}
    </div>
  );
}

function ResultRow({
  item,
  highlight,
  onSelect,
  testId,
  record,
}: {
  item: DropdownItem;
  highlight: boolean;
  onSelect: (value: string) => void;
  testId?: string;
  record?: DropdownRecord;
}) {
  return (
    <button
      type="button"
      role="option"
      data-testid={testId ? `${testId}-option-${item.value}` : undefined}
      data-action={record?.action}
      data-record-value={record?.valueKey}
      value={record ? item.value : undefined}
      onMouseDown={(e) => {
        // Keep focus on input so blur doesn't fire first.
        e.preventDefault();
      }}
      onClick={() => onSelect(item.value)}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '8px 12px',
        border: 'none',
        background: highlight ? 'rgba(254, 243, 199, 0.4)' : 'transparent',
        cursor: 'pointer',
        fontSize: 12,
        textAlign: 'left',
        color: 'var(--cm-gray-900, #111827)',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          highlight ? 'rgba(254, 243, 199, 0.7)' : 'var(--cm-gray-50, #f3f4f6)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = highlight
          ? 'rgba(254, 243, 199, 0.4)'
          : 'transparent';
      }}
    >
      <span
        style={{
          fontFamily: 'Menlo, Monaco, monospace',
          fontWeight: 700,
          minWidth: 48,
          color: 'var(--cm-gray-900, #111827)',
        }}
      >
        {item.value}
      </span>
      <span style={{ flex: 1, color: 'var(--cm-gray-700, #374151)' }}>
        {item.label}
      </span>
      {item.meta && (
        <span
          style={{
            color: 'var(--cm-gray-400, #9ca3af)',
            fontSize: 11,
          }}
        >
          {item.meta}
        </span>
      )}
    </button>
  );
}

export default SearchableDropdown;
